import { useState, useEffect, useRef } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useTimer } from '../useTimer'
import { useMemoryGame } from './useMemoryGame'
import {
  type GameMode,
  type Difficulty,
  type Card,
  MODE_ICONS,
  DIFFICULTY_PAIRS,
} from './gameData'
import { activities, themes } from '../../api/client'
import type { ThemeSummary } from '../../api/client'
import type { GameItem } from '../../i18n'
import { toGameItems, toGameItemsFromTheme } from '../utils'
import GameSetupScreen from '../GameSetupScreen'
import SaveErrorBanner from '../SaveErrorBanner'
import { ss } from '../gameSetupStyles'
import GamePlayHeader from '../GamePlayHeader'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import { COLORS } from '../../styles/tokens'

interface MemoryConfig {
  mode:       GameMode
  difficulty: Difficulty
  themeSlug:  string | null
  themeId:    number | null
}

interface MemoryGameProps {
  onBack: () => void
  onGoToSubscription: () => void
}

type GameScreen = 'setup' | 'playing' | 'complete'

const MODES = Object.keys(MODE_ICONS) as GameMode[]
const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

export default function MemoryGame({ onBack, onGoToSubscription }: MemoryGameProps) {
  const { t, language } = useTranslation()
  const sessionMode = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<MemoryConfig>('memory', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen, setScreen] = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [mode, setMode] = useState<GameMode>((sessionMode?.config.mode as GameMode) ?? restored?.mode ?? 'images-images')
  const [difficulty, setDifficulty] = useState<Difficulty>((sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy')

  // Items fetched from the API; falls back to i18n items if the request fails
  const [classicItems, setClassicItems] = useState<GameItem[]>(t.items)
  const [themeItems, setThemeItems] = useState<GameItem[] | null>(null)
  const [availableThemes, setAvailableThemes] = useState<ThemeSummary[]>([])
  const [selectedTheme, setSelectedTheme] = useState<{ slug: string; id: number } | null>(
    (sessionMode?.config.theme as { slug: string; id: number } | null | undefined) ??
    (restored?.themeSlug != null ? { slug: restored.themeSlug, id: restored.themeId! } : null)
  )
  const [themeLoading, setThemeLoading] = useState(false)

  // When no theme is active, fall back to classic items
  const gameItems = (selectedTheme && themeItems) ?? classicItems

  // Fetch classic items + available themes on mount / language change
  useEffect(() => {
    activities.get('memory')
      .then((detail) => {
        const apiItems = detail.data?.items
        if (apiItems && apiItems.length > 0) {
          setClassicItems(toGameItems(apiItems, language))
        }
      })
      .catch(() => { /* keep i18n fallback */ })

    themes.list('memory')
      .then(setAvailableThemes)
      .catch(() => {})
  }, [language])

  // Fetch theme images whenever the selected theme changes
  useEffect(() => {
    if (!selectedTheme) return
    Promise.resolve().then(async () => {
      setThemeLoading(true)
      try {
        const theme = await themes.get(selectedTheme.slug)
        setThemeItems(toGameItemsFromTheme(theme.images, language))
      } catch {
        setSelectedTheme(null)
      } finally {
        setThemeLoading(false)
      }
    })
  }, [selectedTheme, language])

  const { state, flipCard, resetGame } = useMemoryGame(mode, difficulty, gameItems)
  const { gameComplete, moves, startTime, endTime } = state
  const pairsMatched = state.matchedPairIds.size

  const { sessionId } = useGameSession({
    activitySlug: 'memory',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  // In session mode: reinitialize game state once the configured theme finishes loading.
  // The hook initializes with classic items on mount (theme not yet fetched); this corrects it.
  const sessionThemeResetDone = useRef(false)
  useEffect(() => {
    if (!sessionMode?.config.theme && !restored?.themeSlug) return
    if (sessionThemeResetDone.current) return
    if (!themeItems) return
    sessionThemeResetDone.current = true
    resetGame()
  }, [themeItems, sessionMode, resetGame]) // eslint-disable-line react-hooks/exhaustive-deps

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug: 'memory',
    difficulty,
    mode,
    moves,
    startTime,
    endTime,
    pairsTotal:   DIFFICULTY_PAIRS[difficulty],
    pairsMatched,
    themeId:      selectedTheme?.id,
    sessionId,
    onCompleted:  () => { clear(); setScreen('complete') },
  })

  const handleStart = () => {
    resetGame()
    saveConfig({ mode, difficulty, themeSlug: selectedTheme?.slug ?? null, themeId: selectedTheme?.id ?? null })
    setScreen('playing')
  }
  const handlePlayAgain = () => {
    resetGame()
    saveConfig({ mode, difficulty, themeSlug: selectedTheme?.slug ?? null, themeId: selectedTheme?.id ?? null })
    setScreen('playing')
  }
  const handleNewMode = () => { clear(); setScreen('setup') }

  if ((sessionMode || restored?.themeSlug) && selectedTheme && !themeItems) {
    return <div style={styles.loadRoot}>…</div>
  }

  return (
    <div style={styles.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}

      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          difficulty={difficulty}
          availableThemes={availableThemes}
          selectedTheme={selectedTheme}
          themeLoading={themeLoading}
          onModeChange={setMode}
          onDifficultyChange={setDifficulty}
          onThemeChange={(t) => setSelectedTheme(t ? { slug: t.slug, id: t.id } : null)}
          onStart={handleStart}
          onBack={onBack}
          onGoToSubscription={onGoToSubscription}
        />
      )}
      {screen === 'playing' && (
        <PlayScreen
          state={state}
          mode={mode}
          difficulty={difficulty}
          onFlip={flipCard}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}
      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🌟"
          title={t.memory.complete.title}
          subtitle={t.memory.complete.subtitle}
          stats={[
            { value: String(state.moves), label: t.memory.complete.movesLabel },
            { value: formatGameTime(state.startTime, state.endTime), label: t.memory.complete.timeLabel },
            { value: t.memory.difficulty[difficulty].label, label: t.memory.complete.levelLabel },
          ]}
          playAgainLabel={t.memory.complete.playAgain}
          changeLevelLabel={t.memory.complete.changeMode}
          backLabel={t.memory.complete.backToDashboard}
          onPlayAgain={handlePlayAgain}
          onChangeLevel={handleNewMode}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ────────────────────────────────────────────── */
function SetupScreen({ mode, difficulty, availableThemes, selectedTheme, themeLoading, onModeChange, onDifficultyChange, onThemeChange, onStart, onBack, onGoToSubscription }: {
  mode: GameMode; difficulty: Difficulty; availableThemes: ThemeSummary[]
  selectedTheme: { slug: string; id: number } | null; themeLoading: boolean
  onModeChange: (m: GameMode) => void; onDifficultyChange: (d: Difficulty) => void
  onThemeChange: (t: ThemeSummary | null) => void
  onStart: () => void; onBack: () => void; onGoToSubscription: () => void
}) {
  const { t } = useTranslation()
  const mem = t.memory

  return (
    <GameSetupScreen
      title={mem.title}
      subtitle={mem.subtitle}
      backLabel={mem.backToDashboard}
      onBack={onBack}
      availableThemes={availableThemes}
      selectedTheme={selectedTheme}
      onThemeChange={onThemeChange}
      classicEmoji="🃏"
      classicLabel={mem.classicTheme}
      chooseThemeLabel={mem.chooseThemeTitle}
      onGoToSubscription={onGoToSubscription}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...mem.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={mem.chooseDifficultyTitle}
      startLabel={mem.startBtn}
      onStart={onStart}
      themeLoading={themeLoading}
    >
      {/* Mode selector */}
      <section className="fade-up" style={{ animationDelay: '0.1s' }}>
        <h2 style={ss.optionLabel}>{mem.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {MODES.map((id) => {
            const icons = MODE_ICONS[id]
            const cfg   = mem.modes[id]
            return (
              <button
                key={id}
                onClick={() => onModeChange(id)}
                style={{ ...ss.modeCard, ...(mode === id ? ss.modeCardActive : {}) }}
                aria-pressed={mode === id}
              >
                <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{icons.iconA} {icons.iconB}</span>
                <span style={{ fontSize: '1rem', fontWeight: 700, color: '#2E1F14' }}>{cfg.label}</span>
                <span style={{ fontSize: '0.875rem', color: '#6B4C3B', fontWeight: 500, lineHeight: 1.4 }}>{cfg.description}</span>
              </button>
            )
          })}
        </div>
      </section>
    </GameSetupScreen>
  )
}

/* ── Play Screen ─────────────────────────────────────────────── */
interface PlayScreenProps {
  state: ReturnType<typeof useMemoryGame>['state']
  mode: GameMode
  difficulty: Difficulty
  onFlip: (id: number) => void
  onBack: () => void
}

/** Find the column count that maximises square card size while respecting
 *  the orientation rule: portrait → more rows than cols, landscape → more cols than rows. */
function computeGrid(N: number, areaW: number, areaH: number, gap: number) {
  const isPortrait = areaH >= areaW
  let bestCols = 2
  let bestSize = 0

  for (let c = 2; c <= N; c++) {
    const r = Math.ceil(N / c)
    // Enforce orientation rule
    if (isPortrait  && c > r) continue
    if (!isPortrait && c < r) continue

    const sizeFromW = (areaW - gap * (c - 1)) / c
    const sizeFromH = (areaH - gap * (r - 1)) / r
    const size = Math.min(sizeFromW, sizeFromH)

    if (size > bestSize) {
      bestSize = size
      bestCols = c
    }
  }

  return { cols: bestCols, cardSize: Math.floor(bestSize) }
}

function PlayScreen({ state, mode, difficulty, onFlip, onBack }: PlayScreenProps) {
  const { t } = useTranslation()
  const mem = t.memory
  const timeDisplay = useTimer(state.startTime, state.endTime)
  const totalPairs = DIFFICULTY_PAIRS[difficulty]
  const progress = (state.matchedPairIds.size / totalPairs) * 100

  const boardWrapRef = useRef<HTMLDivElement>(null)
  const [grid, setGrid] = useState({ cols: 4, cardSize: 80 })

  useEffect(() => {
    const el = boardWrapRef.current
    if (!el) return
    const N   = state.cards.length
    const GAP = 10
    const PAD = 12   // padding on each side of boardWrap

    const compute = () => {
      const w = el.clientWidth  - PAD * 2
      const h = el.clientHeight - PAD * 2
      if (w <= 0 || h <= 0) return
      setGrid(computeGrid(N, w, h, GAP))
    }

    compute()
    const ro = new ResizeObserver(compute)
    ro.observe(el)
    return () => ro.disconnect()
  }, [state.cards.length])

  return (
    <div style={styles.playRoot}>
      <GamePlayHeader
        title={mem.title}
        subtitle={mem.modes[mode].label}
        stats={[
          { value: state.moves,  label: mem.moves },
          { value: timeDisplay,  label: mem.time },
        ]}
        progress={progress}
        progressNow={state.matchedPairIds.size}
        progressMax={totalPairs}
        onBack={onBack}
        backLabel={mem.backToSetup}
      />

      <div ref={boardWrapRef} style={styles.boardWrap}>
        <div style={{
          display:             'grid',
          gridTemplateColumns: `repeat(${grid.cols}, ${grid.cardSize}px)`,
          gridAutoRows:        `${grid.cardSize}px`,
          gap:                 10,
        }}>
          {state.cards.map((card) => (
            <MemoryCard key={card.id} card={card} cardSize={grid.cardSize} onFlip={onFlip} isLocked={state.isLocked} />
          ))}
        </div>
      </div>
    </div>
  )
}

/* ── Memory Card ─────────────────────────────────────────────── */
interface MemoryCardProps {
  card: Card
  cardSize: number
  onFlip: (id: number) => void
  isLocked: boolean
}

function MemoryCard({ card, cardSize, onFlip, isLocked }: MemoryCardProps) {
  const isActive = card.isFlipped || card.isMatched
  const isDisabled = isLocked || card.isMatched || card.isFlipped

  return (
    <div
      className="card-scene"
      style={styles.cardScene}
      onClick={() => !isDisabled && onFlip(card.id)}
      role="button"
      aria-label={isActive ? (card.altText ?? card.content) : '?'}
      aria-pressed={isActive}
      tabIndex={isDisabled ? -1 : 0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
          e.preventDefault()
          onFlip(card.id)
        }
      }}
    >
      <div
        className={[
          'card-inner',
          card.isFlipped || card.isMatched ? 'is-flipped' : '',
          card.isMatched ? 'is-matched' : '',
        ].filter(Boolean).join(' ')}
        style={styles.cardInner}
      >
        <div className="card-face card-back-face" style={styles.cardFace} />
        <div className={`card-face card-front-face${card.isMatched ? ' is-matched-face' : ''}`} style={styles.cardFace}>
          <CardContent card={card} cardSize={cardSize} />
        </div>
      </div>
    </div>
  )
}

function CardContent({ card, cardSize }: { card: Card; cardSize: number }) {
  if (card.displayType === 'image') {
    return (
      <img
        src={card.content}
        alt=""
        style={styles.cardImage}
        draggable={false}
      />
    )
  }
  if (card.displayType === 'emoji') {
    return (
      <span style={{ ...styles.cardEmoji, fontSize: Math.round(cardSize * 0.72) }}>
        {card.content}
      </span>
    )
  }
  if (card.displayType === 'word') {
    const fontSize = Math.max(12, Math.round(cardSize * 0.22))
    return <span style={{ ...styles.cardWord, fontSize }}>{card.content}</span>
  }
  const fontSize = Math.max(10, Math.round(cardSize * 0.14))
  return <span style={{ ...styles.cardDefinition, fontSize }}>{card.content}</span>
}

/* ── Styles ──────────────────────────────────────────────────── */
const styles: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh' },
  loadRoot: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', background: COLORS.bgPage, color: '#888' },

  playRoot: { height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  boardWrap: {
    flex:           1,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        12,
    overflow:       'hidden',
  },

  cardScene: { width: '100%', height: '100%', cursor: 'pointer', userSelect: 'none' },
  cardInner: { width: '100%', height: '100%' },
  cardFace: { width: '100%', height: '100%' },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    borderRadius: 6,
    display: 'block',
    userSelect: 'none' as const,
    background: '#fff',
  },
  cardEmoji: { lineHeight: 1, userSelect: 'none' },
  cardWord: {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 700,
    color: COLORS.greenDark,
    textAlign: 'center',
    lineHeight: 1.3,
    padding: '4px 8px',
  },
  cardDefinition: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 600,
    color: '#3D2B1F',
    textAlign: 'center',
    lineHeight: 1.4,
    padding: '6px 8px',
  },

}
